// Course runner — small state machine that walks a Course's steps
// against the live sandbox. The hook owns:
//
//   - the active course (or `null` if no course is running)
//   - the current step index
//   - a `narration` value that the App passes down to <Mascot/> so
//     the speech bubble appears with a Continue button at exactly
//     the right ticks
//
// The runner uses three sandbox affordances:
//
//   - `sandbox.runExperiment(json)` — overlays a spec onto the
//     world. Doesn't reset state (see `experiment.rs::run_experiment`),
//     so a mid-course modifier spec layers cleanly over whatever the
//     simulator is showing.
//   - `sandbox.tickCount()` — used to time "wait" steps.
//   - the App's `setPaused` setter — paused during narrate steps,
//     unpaused during wait steps so the world animates normally.
//
// The hook does NOT call `tick()` itself: the SimCanvas render loop
// is already doing that. Wait-step polling just watches the tick
// counter cross the budget and then re-pauses.

import { useCallback, useEffect, useRef, useState } from "react";

import type { SandboxLike } from "../sim/types";
import type { MascotAnimation } from "./mascot-animation";
import type { Course } from "./types";

/** Speech bubble payload pushed down to <Mascot/>. The Mascot
 *  renders a narration bubble whenever this is non-null. */
export interface CourseNarration {
  /** Display title (the course title, e.g. "Boil Water"). */
  courseTitle: string;
  /** "Step 3 / 8" — pure progress label. */
  progress: string;
  /** Axie's line. */
  says: string;
  /** Pose hint for the mascot canvas. */
  animation?: MascotAnimation;
  /** Click handler for the Continue button. */
  onContinue: () => void;
  /** Click handler for the Exit Course button. */
  onCancel: () => void;
}

export interface CourseRunnerApi {
  /** True while a course is running. */
  active: boolean;
  /** The full active course (for headers / debugging). */
  course: Course | null;
  /** What's currently on Axie's bubble, or null when the runner is
   *  in a non-narration phase. */
  narration: CourseNarration | null;
  /** Begin a course. Cancels any course already in flight. */
  start: (course: Course) => void;
  /** Abort the active course. Pauses the sim and clears narration. */
  stop: () => void;
}

export function useCourseRunner(
  sandbox: SandboxLike | null,
  setPaused: (next: boolean) => void,
): CourseRunnerApi {
  const [course, setCourse] = useState<Course | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [narration, setNarration] = useState<CourseNarration | null>(null);

  // Ref so the wait-step polling loop sees the latest stepIdx
  // without re-subscribing on every increment. (React's state-batch
  // semantics mean the closure captured at setInterval-creation
  // time would otherwise read a stale value.)
  const stepIdxRef = useRef(stepIdx);
  stepIdxRef.current = stepIdx;

  const stop = useCallback(() => {
    setCourse(null);
    setStepIdx(0);
    setNarration(null);
    setPaused(true);
  }, [setPaused]);

  const start = useCallback(
    (next: Course) => {
      // Clear any existing course state first so a fresh start can't
      // race against a stale wait-step poller.
      setNarration(null);
      setStepIdx(0);
      setCourse(next);
      setPaused(true);
    },
    [setPaused],
  );

  // Main step-runner. Re-runs every time stepIdx or course changes.
  // Decides what to do with the *current* step:
  //
  //   experiment → run it synchronously, advance to the next step
  //   narrate    → pause sim, build a narration payload, wait for
  //                the user to click Continue (which advances)
  //   wait       → unpause sim, kick off a polling loop that
  //                advances once the tick budget is spent
  //
  // The last step is always treated as the "course end" — narrate
  // steps offer a Continue that resolves to `stop()` once we hit
  // the end of the array.
  useEffect(() => {
    if (!course || !sandbox) return;
    if (stepIdx >= course.steps.length) {
      // Off the end — drop the course gracefully.
      stop();
      return;
    }
    const step = course.steps[stepIdx];

    if (step.kind === "experiment") {
      const err = sandbox.runExperiment(JSON.stringify(step.spec));
      if (err !== "") {
        // Engine rejected the spec; surface as a narration so the
        // user isn't left staring at a frozen canvas, then bail.
        setNarration({
          courseTitle: course.title,
          progress: `Step ${stepIdx + 1} / ${course.steps.length}`,
          says: `Hmm, the engine couldn't run that step (${err}). Let's stop the course here.`,
          animation: "idle",
          onContinue: stop,
          onCancel: stop,
        });
        setPaused(true);
        return;
      }
      // Make sure the sim is paused so the user sees the static
      // setup before the next narration / wait kicks in.
      setPaused(true);
      setNarration(null);
      setStepIdx((i) => i + 1);
      return;
    }

    if (step.kind === "narrate") {
      setPaused(true);
      const isLast = stepIdx === course.steps.length - 1;
      const advance = () => {
        if (isLast) {
          stop();
        } else {
          setNarration(null);
          setStepIdx((i) => i + 1);
        }
      };
      setNarration({
        courseTitle: course.title,
        progress: `Step ${stepIdx + 1} / ${course.steps.length}`,
        says: step.says,
        animation: step.animation,
        onContinue: advance,
        onCancel: stop,
      });
      return;
    }

    if (step.kind === "wait") {
      setNarration(null);
      setPaused(false);
      const startedAt = sandbox.tickCount();
      const budget = step.ticks;
      // Poll the tick counter every 40 ms — fast enough that the
      // wait feels precise, slow enough that the polling cost is
      // negligible against the render-loop's per-frame work.
      const id = window.setInterval(() => {
        if (stepIdxRef.current !== stepIdx) {
          // Step changed under us (e.g. user hit Exit) — bail.
          window.clearInterval(id);
          return;
        }
        if (sandbox.tickCount() - startedAt >= budget) {
          window.clearInterval(id);
          setPaused(true);
          setStepIdx((i) => i + 1);
        }
      }, 40);
      return () => window.clearInterval(id);
    }
  }, [course, stepIdx, sandbox, setPaused, stop]);

  return {
    active: course !== null,
    course,
    narration,
    start,
    stop,
  };
}
